import { useEffect, useRef, useState } from "react";

export interface useFetchOptions<T, Params extends Array<any>> {
    initialValue?: T,
    placeholderValue?: T | ((prev?: T) => T | undefined),
    onFetch?: (...args: Params) => void,
    onSuccess?: (v: T) => void,
    onError?: (e: Error) => void,
    debounceMs?: number
}

export default function useFetch<T, Params extends Array<any>>(fetchFn: (...args: Params) => Promise<T>, {
    initialValue,
    placeholderValue,
    onFetch = () => { },
    onSuccess = () => { },
    onError = () => { },
    debounceMs = 0,
}: useFetchOptions<T, Params> = {}) {
    //Values
    const [value, setValue] = useState<T | undefined>(initialValue);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    //Ref
    const callIdRef = useRef(0);

    const handleFetch = (...args: Params) => {
        setLoading(true);
        setError(undefined);
        setValue(placeholderValue);

        callIdRef.current += 1;
        const currentCallId = callIdRef.current;

        const fetch = async () => {
            try {
                const v = await fetchFn(...args);
                if (currentCallId === callIdRef.current) {
                    setLoading(false);
                    setValue(v);
                    onSuccess(v);
                }
                return v;
            } catch (e: any) {
                if (currentCallId === callIdRef.current) {
                    setLoading(false);
                    setError(e);
                    onError(e);
                }
                throw e;
            }
        }

        const startFetch = () => {
            onFetch(...args);
            return fetch();
        }

        if (debounceMs) {
            setTimeout(() => {
                if (currentCallId !== callIdRef.current) return;
                return startFetch();
            }, debounceMs);
        } else {
            return startFetch();
        }
    }

    const handleReset = () => {
        callIdRef.current += 1;
        setError(undefined);
        setValue(initialValue);
        setLoading(false);
    }

    useEffect(() => {
        return () => {
            callIdRef.current = -9999
        }
    }, [])

    return {
        fetch: handleFetch,
        reset: handleReset,
        value,
        loading,
        error
    };
}